import { useMemo } from 'react';
import {
  Card,
  StatCard,
  Grid4,
  Select,
  LoadingSpinner,
  RankingTable,
  MetricWithTooltip,
} from './ui';

const legacyMetricDefinitions = {
  adoptionLevel: "Average self-reported usage of AI tools by employees on a scale of 0-100.",
  adoptionRate: "Percentage of employees who have used at least one AI tool in their work.",
  aiKnowledge: "Average score on the company-wide AI literacy quiz (out of 5).",
  learningInterest: "Percentage of employees who expressed active interest in AI training.",
  atiScore: "AI-Technology Interaction (ATI) score, measuring overall affinity and positive interaction with technology (0-100).",
  optimismScore: "Average sentiment towards AI's future impact on the company and jobs (0-10).",
  aiTrust: "Average self-reported trust in the accuracy and reliability of AI systems (0-10).",
  aiFear: "Average self-reported concern or fear about AI's negative impacts (0-10).",
};

const STAT_CARD_COLORS = [
  'text-green-600 dark:text-success',
  'text-purple-600',
  'text-blue-600',
  'text-text-primary-light dark:text-text-primary',
];

function formatMetricValue(value, metricDef) {
  if (value == null || isNaN(value)) return '\u2014';
  const decimals = metricDef?.decimals ?? 1;
  const formatted = Number(value).toFixed(decimals);
  const unit = metricDef?.unit || '';
  if (unit === '%') return `${formatted}%`;
  return `${formatted}${unit}`;
}

export default function BreakdownView({
  categoryType,
  categories,
  selectedCategory,
  onSelectCategory,
  breakdownData,
  loading,
  error,
  onCompanyClick,
  databaseId,
  schema,
}) {
  const isDynamic = !!databaseId && !!schema;
  const schemaMetrics = schema?.metrics || [];

  // Build metric lookup from schema
  const metricLookup = useMemo(() => {
    const map = {};
    schemaMetrics.forEach(m => { map[m.key] = m; });
    return map;
  }, [schemaMetrics]);

  // First 4 schema metrics for stat cards and table columns
  const dynamicDisplayMetrics = useMemo(() => {
    if (!isDynamic) return null;
    return schemaMetrics.slice(0, 4).map((m, i) => ({
      key: m.key,
      label: m.label,
      unit: m.unit || '',
      decimals: m.decimals ?? 1,
      description: m.description || (m.label + (m.unit ? ` (${m.unit})` : '')),
      color: STAT_CARD_COLORS[i] || 'font-semibold',
    }));
  }, [isDynamic, schemaMetrics]);

  // Dynamic metric definitions for tooltips
  const metricDefinitions = useMemo(() => {
    if (!isDynamic) return legacyMetricDefinitions;
    const defs = { ...legacyMetricDefinitions };
    schemaMetrics.forEach(m => {
      defs[m.key] = m.description || (m.label + (m.unit ? ` (${m.unit})` : ''));
    });
    return defs;
  }, [isDynamic, schemaMetrics]);

  const formatCategoryType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getPerformanceColor = (difference) => {
    if (difference > 0) return 'success';
    if (difference < 0) return 'danger';
    return 'default';
  };

  // Helper to read metric value from a company doc
  const getCompanyMetric = (company, key) => {
    if (isDynamic && databaseId) {
      return company.databases?.[databaseId]?.metrics?.[key] ?? company.metrics?.[key] ?? 0;
    }
    return company.metrics?.[key] ?? 0;
  };

  if (!selectedCategory) {
    return (
      <Card>
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
            Select {formatCategoryType(categoryType)}
          </label>
          <Select value="" onChange={(e) => onSelectCategory(e.target.value)}>
            <option value="">Choose a {categoryType}...</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Select>
        </div>
        <div className="text-center py-8 text-text-muted-light dark:text-text-muted">
          Select a {categoryType} to view detailed breakdown and company list
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="danger">
        <p className="text-danger-dark">Error loading breakdown data</p>
      </Card>
    );
  }

  if (!breakdownData) {
    return null;
  }

  // --- Dynamic stat cards ---
  const renderDynamicStatCards = () => {
    if (!dynamicDisplayMetrics) return null;
    return (
      <Grid4 className="mb-6">
        {dynamicDisplayMetrics.map((metric) => {
          const catValue = breakdownData.categoryMetrics?.[metric.key];
          const overallValue = breakdownData.overallMetrics?.[metric.key];
          const diff = breakdownData.differences?.[metric.key];
          const metricDef = metricLookup[metric.key] || metric;

          return (
            <StatCard
              key={metric.key}
              title={
                <MetricWithTooltip
                  label={metric.label}
                  definition={metric.description}
                />
              }
              value={formatMetricValue(catValue, metricDef)}
              subtitle={`Overall: ${formatMetricValue(overallValue, metricDef)} (${
                diff > 0 ? '+' : ''
              }${formatMetricValue(diff, metricDef)})`}
              valueColor={getPerformanceColor(diff)}
            />
          );
        })}
      </Grid4>
    );
  };

  // --- Legacy stat cards ---
  const renderLegacyStatCards = () => (
    <Grid4 className="mb-6">
      <StatCard
        title={
          <MetricWithTooltip
            label="Adoption Rate"
            definition={metricDefinitions.adoptionRate}
          />
        }
        value={`${breakdownData.categoryMetrics.avgAdoptionRate.toFixed(1)}%`}
        subtitle={`Overall: ${breakdownData.overallMetrics.avgAdoptionRate.toFixed(1)}% (${
          breakdownData.differences.adoptionRate > 0 ? '+' : ''
        }${breakdownData.differences.adoptionRate.toFixed(1)}pp)`}
        valueColor={getPerformanceColor(breakdownData.differences.adoptionRate)}
      />

      <StatCard
        title={
          <MetricWithTooltip
            label="ATI Score"
            definition={metricDefinitions.atiScore}
          />
        }
        value={`${breakdownData.categoryMetrics.avgATIScore.toFixed(1)}`}
        subtitle={`Overall: ${breakdownData.overallMetrics.avgATIScore.toFixed(1)} (${
          breakdownData.differences.atiScore > 0 ? '+' : ''
        }${breakdownData.differences.atiScore.toFixed(1)})`}
        valueColor={getPerformanceColor(breakdownData.differences.atiScore)}
      />

      <StatCard
        title={
          <MetricWithTooltip
            label="AI Knowledge"
            definition={metricDefinitions.aiKnowledge}
          />
        }
        value={`${breakdownData.categoryMetrics.avgAIKnowledge.toFixed(2)}`}
        subtitle={`Overall: ${breakdownData.overallMetrics.avgAIKnowledge.toFixed(2)} (${
          breakdownData.differences.aiKnowledge > 0 ? '+' : ''
        }${breakdownData.differences.aiKnowledge.toFixed(2)})`}
        valueColor={getPerformanceColor(breakdownData.differences.aiKnowledge)}
      />

      <StatCard
        title={
          <MetricWithTooltip
            label="Optimism Score"
            definition={metricDefinitions.optimismScore}
          />
        }
        value={`${breakdownData.categoryMetrics.avgOptimismScore.toFixed(1)}`}
        subtitle={`Overall: ${breakdownData.overallMetrics.avgOptimismScore.toFixed(1)} (${
          breakdownData.differences.optimismScore > 0 ? '+' : ''
        }${breakdownData.differences.optimismScore.toFixed(1)})`}
        valueColor={getPerformanceColor(breakdownData.differences.optimismScore)}
      />
    </Grid4>
  );

  // --- Dynamic ranking table ---
  const renderDynamicRankingTable = () => {
    if (!dynamicDisplayMetrics) return null;

    const headers = [
      { label: 'Rank', align: 'left' },
      { label: 'Company', align: 'left' },
      ...dynamicDisplayMetrics.map((metric) => ({
        label: (
          <MetricWithTooltip
            label={metric.label}
            definition={metric.description}
          />
        ),
        align: 'right',
      })),
    ];

    const columns = [
      {
        key: 'rank',
        align: 'left',
        className: 'font-semibold text-brand-primary',
        render: (_, index) => `#${index + 1}`,
        sortValue: (_, index) => index + 1,
      },
      {
        key: 'name',
        align: 'left',
        className: 'text-text-primary-light dark:text-text-primary font-medium cursor-pointer hover:opacity-70 transition',
      },
      ...dynamicDisplayMetrics.map((metric, i) => ({
        key: metric.key,
        align: 'right',
        className: `font-semibold ${metric.color}`,
        render: (company) => {
          const value = getCompanyMetric(company, metric.key);
          return formatMetricValue(value, metricLookup[metric.key] || metric);
        },
        sortValue: (company) => getCompanyMetric(company, metric.key),
      })),
    ];

    return (
      <RankingTable
        cellPadding="compact"
        sortable={true}
        defaultSortColumn={2}
        defaultSortDirection="desc"
        headers={headers}
        data={breakdownData.companies.map(company => ({
          ...company,
          onClick: onCompanyClick ? () => onCompanyClick(company) : undefined,
        }))}
        columns={columns}
      />
    );
  };

  // --- Legacy ranking table ---
  const renderLegacyRankingTable = () => (
    <RankingTable
      cellPadding="compact"
      sortable={true}
      defaultSortColumn={2}
      defaultSortDirection="desc"
      headers={[
        { label: 'Rank', align: 'left' },
        { label: 'Company', align: 'left' },
        {
          label: (
            <MetricWithTooltip
              label="Adoption Rate"
              definition={metricDefinitions.adoptionRate}
            />
          ),
          align: 'right',
        },
        {
          label: (
            <MetricWithTooltip
              label="ATI Score"
              definition={metricDefinitions.atiScore}
            />
          ),
          align: 'right',
        },
        {
          label: (
            <MetricWithTooltip
              label="AI Knowledge"
              definition={metricDefinitions.aiKnowledge}
            />
          ),
          align: 'right',
        },
        {
          label: (
            <MetricWithTooltip
              label="Optimism"
              definition={metricDefinitions.optimismScore}
            />
          ),
          align: 'right',
        },
      ]}
      data={breakdownData.companies.map(company => ({
        ...company,
        onClick: onCompanyClick ? () => onCompanyClick(company) : undefined
      }))}
      columns={[
        {
          key: 'rank',
          align: 'left',
          className: 'font-semibold text-brand-primary',
          render: (_, index) => `#${index + 1}`,
          sortValue: (_, index) => index + 1,
        },
        {
          key: 'name',
          align: 'left',
          className: 'text-text-primary-light dark:text-text-primary font-medium cursor-pointer hover:opacity-70 transition',
        },
        {
          key: 'adoptionRate',
          align: 'right',
          className: 'font-semibold text-green-600 dark:text-success',
          render: (company) => {
            const rate = company.metrics?.adoptionRate || 0;
            return `${rate.toFixed(1)}%`;
          },
          sortValue: (company) => company.metrics?.adoptionRate || 0,
        },
        {
          key: 'atiScore',
          align: 'right',
          className: 'font-semibold text-purple-600',
          render: (company) => {
            const score = company.metrics?.avgATIScore || 0;
            return `${score.toFixed(1)}`;
          },
          sortValue: (company) => company.metrics?.avgATIScore || 0,
        },
        {
          key: 'aiKnowledge',
          align: 'right',
          className: 'font-semibold text-blue-600',
          render: (company) => {
            const knowledge = company.metrics?.avgAIKnowledge || 0;
            return `${knowledge.toFixed(2)}`;
          },
          sortValue: (company) => company.metrics?.avgAIKnowledge || 0,
        },
        {
          key: 'optimism',
          align: 'right',
          className: 'font-semibold text-text-primary-light dark:text-text-primary',
          render: (company) => {
            const optimism = company.metrics?.avgOptimismScore || 0;
            return `${optimism.toFixed(1)}`;
          },
          sortValue: (company) => company.metrics?.avgOptimismScore || 0,
        },
      ]}
    />
  );

  return (
    <>
      {/* Category Selection */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
              Selected {formatCategoryType(categoryType)}
            </label>
            <Select value={selectedCategory} onChange={(e) => onSelectCategory(e.target.value)}>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Select>
          </div>
          <div className="text-right">
            <div className="text-sm text-text-secondary-light dark:text-text-secondary">
              Companies in {categoryType}
            </div>
            <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
              {breakdownData.companyCount}
            </div>
          </div>
        </div>
      </Card>

      {/* No companies message */}
      {breakdownData.companyCount === 0 && (
        <Card>
          <div className="text-center py-8 text-text-muted-light dark:text-text-muted">
            No companies found in {selectedCategory}
          </div>
        </Card>
      )}

      {/* Stat Cards: Category vs Overall */}
      {breakdownData.companyCount > 0 && (
        <>
          <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
            {selectedCategory} vs Overall Performance
          </h2>

          {isDynamic ? renderDynamicStatCards() : renderLegacyStatCards()}

          {/* Companies Table */}
          <Card>
            <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary mb-4">
              Companies in {selectedCategory}
            </h3>
            {isDynamic ? renderDynamicRankingTable() : renderLegacyRankingTable()}
          </Card>
        </>
      )}
    </>
  );
}
